import {
  FunctionComponent,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { CardElement } from "@stripe/react-stripe-js";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LockClosedIcon } from "@heroicons/react/20/solid";
import { useNumericFormat } from "react-number-format";
import PropTypes from "prop-types";
import z from "zod";

import { useFeedSignupPageDataQuery } from "@helloaudio/api";
import { Checkbox, Label, TextInput } from "@helloaudio/ui";
import { getCurrencySymbol } from "@helloaudio/utils";

import PricingRadioGroup from "./PricingRadioGroup";

export enum PaymentType {
  Free,
  OneTime,
  Recurring,
}

export enum PaymentFrequency {
  Weekly = "week",
  Monthly = "month",
  Annually = "year",
}

export const useSignupForm = (feedId: string) => {
  const { data } = useFeedSignupPageDataQuery(feedId);
  const schema = useMemo(() => {
    return z.object({
      feedId: z.string(),
      step: z.number(),
      pricingOptionId: z.string(),
      name:
        data!.shouldAskForName && data!.shouldRequireName
          ? z.string().min(1, "This is a required field")
          : z.string().optional(),
      email: z.string().min(1, "This is a required field").email(),
      agreedToAgreementText: data!.shouldRequireAgreement
        ? z.boolean().refine((arg) => arg)
        : z.boolean(),
      phone:
        data!.shouldAskForPhone && data!.shouldRequirePhone
          ? z.string().min(1, "This is a required field")
          : z.string().optional(),
    });
  }, [data]);

  const methods = useForm<z.infer<typeof schema>>({
    mode: "onTouched",
    defaultValues: {
      feedId,
      step: 0,
      pricingOptionId: data!.pricingOptions[0]?.id || undefined,
      agreedToAgreementText: false,
      name: "",
      email: "",
      phone: "",
    },
    resolver: zodResolver(schema),
  });

  const [step, pricingOptionId] = methods.watch(["step", "pricingOptionId"]);
  const pricingOption = useMemo(() => {
    return data!.pricingOptions.find((pricingOption) => pricingOption.id === pricingOptionId);
  }, [pricingOptionId]);

  // If user has selected FREE option, change form step to 0
  useEffect(() => {
    if (pricingOption?.paymentType === PaymentType.Free) {
      methods.setValue("step", 0);
    }
  }, [pricingOption]);

  const next = async (e?: SyntheticEvent) => {
    e?.preventDefault();
    switch (step) {
      case 0: {
        const valid = await methods.trigger(["name", "email", "phone"], { shouldFocus: true });
        if (valid) methods.setValue("step", 1);
        return;
      }
    }
  };

  const previous = async (e?: SyntheticEvent) => {
    e?.preventDefault();
    switch (step) {
      case 1: {
        methods.setValue("step", 0);
        return;
      }
    }
  };

  return {
    ...methods,
    pricingOption,
    step,
    next,
    previous,
  };
};

type SignupFormMethods = Omit<
  ReturnType<typeof useSignupForm>,
  "pricingOption" | "step" | "next" | "previous"
>;
type SignupFormProps = SignupFormMethods & {
  children?: ReactNode;
};

type SignupFormComponent = FunctionComponent<SignupFormProps> & {
  PricingOptionsOptionGroup: FunctionComponent<{ className?: string }>;
  NameField: FunctionComponent<{ className?: string }>;
  EmailField: FunctionComponent<{ className?: string }>;
  PhoneField: FunctionComponent<{ className?: string }>;
  AgreementCheckbox: FunctionComponent<{ className?: string }>;
  CardField: FunctionComponent<{ className?: string }>;
};

/**
 * The form itself, acts as provider
 * @param param0
 * @returns
 */
const SignupForm: SignupFormComponent = ({ children, ...methods }) => {
  return <FormProvider {...methods}>{children}</FormProvider>;
};
SignupForm.propTypes = {
  children: PropTypes.node,
};

/**
 * Pricing Options option group
 * @returns
 */
SignupForm.PricingOptionsOptionGroup = () => {
  const { watch, control }: SignupFormMethods = useFormContext();
  const feedId = watch("feedId");
  const { data } = useFeedSignupPageDataQuery(feedId);
  const pricingOptions = data!.pricingOptions;

  const { format: formatPrice } = useNumericFormat({
    decimalScale: 2,
    fixedDecimalScale: true,
    thousandSeparator: true,
  });

  // Returns: FREE or $10.00/month
  const formatPriceText = useCallback(
    (pricingOption: (typeof pricingOptions)[0]) => {
      if (pricingOption.paymentType === PaymentType.Free) {
        return "FREE";
      }

      const currency = getCurrencySymbol(pricingOption.currency);
      const price = formatPrice((pricingOption.paymentAmount / 100).toString());
      if (pricingOption.paymentType === PaymentType.OneTime) {
        return currency + price;
      }

      const frequency = pricingOption.paymentFrequency;
      return currency + price + "/" + frequency;
    },
    [formatPrice],
  );

  // Returns: undefined or For 8 months
  const formatSubPriceText = useCallback((pricingOption: (typeof pricingOptions)[0]) => {
    if (
      pricingOption.paymentType === PaymentType.Free ||
      pricingOption.paymentType === PaymentType.OneTime ||
      (pricingOption.paymentType === PaymentType.Recurring && pricingOption.repeatPayment === false)
    ) {
      return undefined;
    }

    return `For ${pricingOption.numberOfPayments} ${pricingOption.paymentFrequency}s`;
  }, []);

  return pricingOptions.length === 0 ? (
    <div>There are no pricing options available</div>
  ) : (
    <Controller
      control={control}
      name="pricingOptionId"
      render={({ field }) => (
        <PricingRadioGroup
          value={field.value}
          onChange={field.onChange}
          label={pricingOptions.length === 1 ? "Price" : "Choose Your Payment Option"}
          options={pricingOptions.map((pricingOption) => ({
            key: pricingOption.id,
            value: pricingOption.id,
            label: pricingOption.label,
            priceText: formatPriceText(pricingOption),
            subPriceText: formatSubPriceText(pricingOption),
          }))}
        />
      )}
    />
  );
};
SignupForm.PricingOptionsOptionGroup.displayName = "SignupForm.PricingOptionsOptionGroup";

/**
 * Name field
 * @returns
 */
SignupForm.NameField = ({ className }) => {
  const {
    watch,
    register,
    formState: { errors },
  }: SignupFormMethods = useFormContext();
  const feedId = watch("feedId");
  const { data } = useFeedSignupPageDataQuery(feedId);

  return data!.shouldAskForName ? (
    <TextInput
      {...register("name")}
      required={data!.shouldRequireName}
      className={className}
      label="Name"
      placeholder="Enter your name"
      error={errors.name?.message}
    />
  ) : null;
};
SignupForm.NameField.displayName = "SignupForm.NameField";
SignupForm.NameField.propTypes = {
  className: PropTypes.string,
};

/**
 * Email field
 * @returns
 */
SignupForm.EmailField = ({ className }) => {
  const {
    register,
    formState: { errors },
  }: SignupFormMethods = useFormContext();

  return (
    <TextInput
      {...register("email")}
      className={className}
      required
      label="Email"
      placeholder="Enter your email"
      type="email"
      error={errors.email?.message}
    />
  );
};
SignupForm.EmailField.displayName = "SignupForm.EmailField";
SignupForm.EmailField.propTypes = {
  className: PropTypes.string,
};

/**
 * Phone field
 * @returns
 */
SignupForm.PhoneField = ({ className }) => {
  const {
    watch,
    register,
    formState: { errors },
  }: SignupFormMethods = useFormContext();
  const feedId = watch("feedId");
  const { data } = useFeedSignupPageDataQuery(feedId);

  return data!.shouldAskForPhone ? (
    <TextInput
      {...register("phone")}
      required={data!.shouldRequirePhone}
      className={className}
      label="Phone"
      placeholder="Enter your phone number"
      type="tel"
      error={errors.phone?.message}
    />
  ) : null;
};
SignupForm.PhoneField.displayName = "SignupForm.PhoneField";
SignupForm.PhoneField.propTypes = {
  className: PropTypes.string,
};

/**
 * Agreement checkbox
 * @returns
 */
SignupForm.AgreementCheckbox = ({ className }) => {
  const { watch, register }: SignupFormMethods = useFormContext();
  const feedId = watch("feedId");
  const { data } = useFeedSignupPageDataQuery(feedId);

  return data!.shouldRequireAgreement && data!.agreementText ? (
    <Checkbox
      {...register("agreedToAgreementText")}
      // className="tw-mt-10 tw-mb-4"
      className={className}
      dangerouslySetLabel
      label={data!.agreementText}
    />
  ) : null;
};
SignupForm.AgreementCheckbox.displayName = "SignupForm.AgreementCheckbox";
SignupForm.AgreementCheckbox.propTypes = {
  className: PropTypes.string,
};

SignupForm.CardField = ({ className }) => {
  return (
    <div className={className}>
      <div className="tw-flex tw-justify-between">
        <Label className="tw-mb-2">Payment Method</Label>
        <span className="tw-font-semibold tw-text-xs tw-text-gray-500">
          Secure <LockClosedIcon className="tw-w-4 tw-h-4" />
        </span>
      </div>

      <CardElement className="tw-p-3 tw-border-solid tw-border tw-border-gray-300 tw-rounded" />
    </div>
  );
};
SignupForm.CardField.displayName = "SignupForm.CardField";
SignupForm.CardField.propTypes = {
  className: PropTypes.string,
};

export default SignupForm;
